"use client"

import clsx from "clsx"
import React from "react"
import { useDataAttributes } from "../../hooks"
import { BaseInteractiveProps } from "../../utils"
import { Switch } from "../shadcn/switch"

/**
 * @description 토글 스위치. on/off 상태 전환
 * @example
 * <TSwitch value={isEnabled} label="알림 받기" onChange={setIsEnabled} />
 */
interface TSwitchProps extends BaseInteractiveProps {
	/**
	 * 현재 on/off 상태
	 * @default false
	 */
	value?: boolean
	/** 스위치 옆에 표시할 라벨 */
	label?: string | React.ReactNode
	/**
	 * 라벨 위치
	 * @ai-style "start"=라벨이 스위치 왼쪽, "end"=라벨이 스위치 오른쪽
	 * @default "end"
	 */
	labelPlacement?: "start" | "end"
	/** 상태 변경 콜백. checked=true이면 on 상태 */
	onChange?: (checked: boolean) => void
}

export const TSwitch = React.memo((props: TSwitchProps) => {
	const { id, value = false, label, labelPlacement = "end", disabled = false, className, dataAttributes, onChange, ...restProps } = props
	const internalId = React.useId()
	const switchId = id ?? internalId
	const dataProps = useDataAttributes(dataAttributes)

	const handleCheckedChange = React.useCallback(
		(next: boolean) => {
			onChange?.(next)
		},
		[onChange]
	)

	return (
		<div className={clsx("TSwitch", className)} {...restProps} data-label-placement={labelPlacement} {...dataProps}>
			<Switch id={switchId} checked={value} disabled={disabled} onCheckedChange={handleCheckedChange} />
			{label && <label htmlFor={switchId}>{label}</label>}
		</div>
	)
})

TSwitch.displayName = "TSwitch"
